import { getSupabaseClient } from '@/infrastructure/supabase/app'

// Private bucket created in 0040_event_photos.sql.
const BUCKET = 'org-event-photos'

const FULL_MAX_SIDE = 1600
const THUMB_MAX_SIDE = 400
const FULL_QUALITY = 0.82
const THUMB_QUALITY = 0.7

/** Cap on the file the user picks, before compression. */
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024
export const ACCEPTED_PHOTO_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp']

export interface PhotoSize {
  readonly width: number
  readonly height: number
}

/** Fits the image inside a square of `maxSide` keeping the aspect ratio; never upscales. */
export function calculateTargetSize(width: number, height: number, maxSide: number): PhotoSize {
  if (width <= 0 || height <= 0) throw new Error('La imagen no tiene dimensiones válidas.')
  if (width <= maxSide && height <= maxSide) return { width, height }
  const ratio = width >= height ? maxSide / width : maxSide / height
  return { width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio)) }
}

export function formatPhotoSize(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function assertPhotoAllowed(file: { type: string; size: number }): void {
  if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
    throw new Error('Formato no permitido. Usa JPG, PNG o WebP.')
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error(`La foto supera el máximo de ${Math.round(MAX_SOURCE_BYTES / 1024 / 1024)} MB.`)
  }
}

// WebP first (roughly 30% smaller than JPEG at the same quality); JPEG as a fallback for browsers
// whose canvas cannot encode it.
async function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  const encode = (type: string) =>
    new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, type, quality)
    })

  const webp = await encode('image/webp')
  if (webp && webp.type === 'image/webp') return webp
  const jpeg = await encode('image/jpeg')
  if (jpeg) return jpeg
  throw new Error('El navegador no pudo comprimir la imagen.')
}

async function render(bitmap: ImageBitmap, target: PhotoSize, quality: number): Promise<Blob> {
  const canvas = document.createElement('canvas')
  canvas.width = target.width
  canvas.height = target.height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('El navegador no pudo procesar la imagen.')
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(bitmap, 0, 0, target.width, target.height)
  return await toBlob(canvas, quality)
}

export interface CompressedPhoto {
  readonly full: Blob
  readonly thumb: Blob
  readonly width: number
  readonly height: number
  readonly originalBytes: number
}

/** Downscales and re-encodes in the browser: a 4 MB phone photo ends up around 300 KB. */
export async function compressPhoto(file: File): Promise<CompressedPhoto> {
  assertPhotoAllowed(file)

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    throw new Error('No se pudo leer la imagen. Si viene de un iPhone, guárdala como JPG.')
  }

  try {
    const fullSize = calculateTargetSize(bitmap.width, bitmap.height, FULL_MAX_SIDE)
    const thumbSize = calculateTargetSize(bitmap.width, bitmap.height, THUMB_MAX_SIDE)
    const [full, thumb] = await Promise.all([render(bitmap, fullSize, FULL_QUALITY), render(bitmap, thumbSize, THUMB_QUALITY)])
    return { full, thumb, width: fullSize.width, height: fullSize.height, originalBytes: file.size }
  } finally {
    bitmap.close()
  }
}

function extensionFor(blob: Blob): string {
  return blob.type === 'image/webp' ? 'webp' : 'jpg'
}

export interface UploadedPhoto {
  readonly storagePath: string
  readonly thumbPath: string
  readonly byteSize: number
  readonly thumbByteSize: number
  readonly width: number
  readonly height: number
  readonly originalBytes: number
}

export async function uploadEventPhoto(input: {
  readonly organizationId: string
  readonly eventId: string
  readonly file: File
}): Promise<UploadedPhoto> {
  const compressed = await compressPhoto(input.file)
  const client = getSupabaseClient()
  const id = crypto.randomUUID()
  const base = `organizations/${input.organizationId}/event-photos/${input.eventId}/${id}`
  const storagePath = `${base}.${extensionFor(compressed.full)}`
  const thumbPath = `${base}-thumb.${extensionFor(compressed.thumb)}`

  const full = await client.storage.from(BUCKET).upload(storagePath, compressed.full, {
    contentType: compressed.full.type,
    upsert: false,
  })
  if (full.error) throw full.error

  const thumb = await client.storage.from(BUCKET).upload(thumbPath, compressed.thumb, {
    contentType: compressed.thumb.type,
    upsert: false,
  })
  if (thumb.error) {
    // Never leave a full-size object without its thumbnail.
    await client.storage.from(BUCKET).remove([storagePath])
    throw thumb.error
  }

  return {
    storagePath,
    thumbPath,
    byteSize: compressed.full.size,
    thumbByteSize: compressed.thumb.size,
    width: compressed.width,
    height: compressed.height,
    originalBytes: compressed.originalBytes,
  }
}

export async function removeEventPhotoObjects(paths: readonly string[]): Promise<void> {
  const client = getSupabaseClient()
  const { error } = await client.storage.from(BUCKET).remove([...paths])
  if (error) throw error
}
